import { chatService } from '@/services/chat.service'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

const PAGE_SIZE = 20

export function useChats() {
  return useQuery({
    queryKey: ['chats'],
    queryFn: () => chatService.getChats(),
  })
}

export function useChat(chatId: string) {
  return useQuery({
    queryKey: ['chats', chatId],
    queryFn: () => chatService.getChat(chatId),
    enabled: !!chatId,
  })
}

export function useCreateChat() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (title: string) => chatService.createChat(title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'], exact: true })
    },
  })
}

export function useDeleteChat() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (chatId: string) => chatService.deleteChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'], exact: true })
    },
  })
}

export function useSendMessage(chatId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (content: string) => chatService.sendMessage(chatId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats', chatId] })
    },
  })
}

export function useMessages(chatId: string) {
  return useQuery({
    queryKey: ['chats', chatId, 'messages'],
    queryFn: () => chatService.getMessages(chatId),
    enabled: !!chatId,
  })
}

export function useInfiniteMessages(chatId: string) {
  return useInfiniteQuery({
    queryKey: ['chats', chatId, 'messages', 'infinite'],
    queryFn: ({ pageParam = 0 }) => chatService.getMessages(chatId, pageParam, PAGE_SIZE),
    enabled: !!chatId,
    // Always refetch on mount so ChatPage shows the latest messages
    refetchOnMount: 'always',
    // Treat messages as immediately stale so navigation back to chat always refreshes
    staleTime: 0,
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If we got fewer messages than requested, there are no more
      if (lastPage.length < PAGE_SIZE) {
        return undefined
      }
      // Return the next skip value (total messages loaded so far)
      return allPages.reduce((total, page) => total + page.length, 0)
    },
  })
}
